/**
 * Video embed URL parsing for form funnel interstitial content pages.
 *
 * Provider-agnostic API: callers pass a raw URL and receive a normalized
 * embed URL ready to drop into an iframe `src`. All provider-specific
 * parsing lives in this file — adding Vimeo/Loom/etc. is a localized change.
 *
 * YouTube uses the `youtube-nocookie.com` embed domain to avoid setting
 * third-party tracking cookies until the user presses play (GDPR-friendlier,
 * and less likely to be blocked by consent managers).
 */

export interface VideoEmbed {
  embedUrl: string;
}

const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Extracts the 11-character YouTube video ID from any of the common URL
 * shapes (watch, youtu.be, embed, shorts, live). Returns null if the URL
 * is not a recognizable YouTube link.
 */
function extractYouTubeId(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, "");

  // youtu.be/<id>
  if (host === "youtu.be") {
    const id = url.pathname.slice(1).split("/")[0];
    return YOUTUBE_ID_PATTERN.test(id) ? id : null;
  }

  // youtube.com / youtube-nocookie.com / m.youtube.com
  if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
    // /watch?v=<id>
    if (url.pathname === "/watch") {
      const id = url.searchParams.get("v") ?? "";
      return YOUTUBE_ID_PATTERN.test(id) ? id : null;
    }
    // /embed/<id>, /shorts/<id>, /live/<id>, /v/<id>
    const match = url.pathname.match(/^\/(embed|shorts|live|v)\/([^/?#]+)/);
    if (match) {
      const id = match[2];
      return YOUTUBE_ID_PATTERN.test(id) ? id : null;
    }
  }

  return null;
}

/**
 * Parses a video URL into a normalized embed URL. Returns null if the URL
 * is empty, malformed, or from an unsupported provider.
 */
export function parseVideoEmbed(rawUrl: string | null | undefined): VideoEmbed | null {
  if (!rawUrl) return null;

  const youtubeId = extractYouTubeId(rawUrl);
  if (youtubeId) {
    return { embedUrl: `https://www.youtube-nocookie.com/embed/${youtubeId}` };
  }

  return null;
}

/**
 * Validator for CMS use. Returns `true` on valid input (or empty, which
 * means the field is unset), or a human-readable error message otherwise.
 *
 * The supported-provider list lives here so the error message stays in
 * sync with the parser as providers are added later.
 */
export function validateVideoEmbedUrl(rawUrl: string | null | undefined): true | string {
  if (!rawUrl) return true;
  if (parseVideoEmbed(rawUrl)) return true;
  return "Unsupported video URL. Supported providers: YouTube.";
}
